import React from 'react'
import { Roles } from '@app/src/wf-constants'
import { Box, Chip, Radio, Stack, Typography } from '@mui/material'
import { useFlags } from 'launchdarkly-react-client-sdk'
import type { Control } from 'react-hook-form'
import { Controller } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { getColorByRole } from '@app/src/utils/colors'
import { insertIf } from '@app/src/utils/helpersTs'

type AvailableRole = {
  value: string
  label: string
  description: string
}

export function RolePicker({ control, name, disabled }: { control: Control; name: string; disabled: boolean }) {
  const { formatMessage } = useIntl()
  const { viewOnly: viewOnlyFlag } = useFlags()

  const availableRoles: AvailableRole[] = [
    {
      value: Roles.Admin,
      label: formatMessage({ id: 'roles.admin.name' }),
      description: formatMessage({ id: 'roles.admin.description' }),
    },
    {
      value: Roles.Contributor,
      label: formatMessage({ id: 'roles.contributor.name' }),
      description: formatMessage({ id: 'roles.contributor.description' }),
    },
    ...insertIf(viewOnlyFlag, {
      value: Roles.ViewOnly,
      label: formatMessage({ id: 'roles.viewonly.name' }),
      description: formatMessage({ id: 'roles.viewonly.description' }),
    }),
  ]

  return (
    <Controller
      control={control}
      name={name}
      render={({ value, onChange }) => (
        <Stack gap={2}>
          {availableRoles.map(role => (
            <Stack
              key={role.value}
              direction="row"
              alignItems="flex-start"
              gap={1.5}
              sx={{
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 1,
              }}
              onClick={() => !disabled && onChange(role.value)}
            >
              <Radio sx={{ p: 0, m: 0 }} checked={value === role.value} disabled={disabled} />

              <Stack direction="column" gap={1} flex={1}>
                <Box>
                  <Chip color={getColorByRole(role.value)} label={role.label} size="small" />
                </Box>

                <Typography variant="body2" color="text.primary">
                  {formatMessage({ id: `roles.${role.value.toLowerCase()}.description` })}
                </Typography>
              </Stack>
            </Stack>
          ))}
        </Stack>
      )}
    />
  )
}
